import React, { useState, useEffect } from 'react';
import { X, Sun, Sliders, Check, RotateCcw, MapPin, Calendar } from 'lucide-react';
import { SunFilterConfig, SunPeriod } from '../../types';

interface SunFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: SunFilterConfig;
  onApply: (config: SunFilterConfig) => void;
  candidateCount?: number | null;
}

const PERIOD_OPTIONS: { id: SunPeriod; label: string; sublabel: string; emoji: string }[] = [
  { id: 'today', label: 'I dag', sublabel: 'Nærmeste sol i dag', emoji: '☀️' },
  { id: 'tomorrow', label: 'I morgen', sublabel: 'Sol i morgen', emoji: '🌤️' },
  { id: '3days', label: '3 dager', sublabel: 'Sol eller opphold', emoji: '📆' },
  { id: 'weekend', label: 'Helgen', sublabel: 'Lørdag & søndag', emoji: '🏕️' },
];

const DISTANCE_PRESETS = [50, 100, 200, 0];

export const SunFilterModal: React.FC<SunFilterModalProps> = ({
  isOpen,
  onClose,
  config,
  onApply,
  candidateCount,
}) => {
  const [period, setPeriod] = useState<SunPeriod>(config.period);
  const [maxDistanceKm, setMaxDistanceKm] = useState<number>(config.maxDistanceKm);
  const [strictSun, setStrictSun] = useState<boolean>(config.strictSun);

  useEffect(() => {
    if (isOpen) {
      setPeriod(config.period);
      setMaxDistanceKm(config.maxDistanceKm);
      setStrictSun(config.strictSun);
    }
  }, [isOpen, config]);

  if (!isOpen) return null;

  const handleReset = () => {
    setPeriod('3days');
    setMaxDistanceKm(200);
    setStrictSun(false);
  };

  const handleApply = () => {
    onApply({
      enabled: true,
      period,
      maxDistanceKm,
      strictSun,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-end md:items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Modal / Sheet Container */}
      <div className="relative z-10 w-full max-w-lg bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-t-3xl md:rounded-3xl shadow-2xl p-4 md:p-6 overflow-hidden max-h-[90vh] flex flex-col animate-in slide-in-from-bottom-4 duration-300">
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-[var(--border-primary)]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0 border border-amber-500/30">
              <Sun size={18} />
            </div>
            <div>
              <h3 className="text-base font-black text-[var(--text-primary)]">
                Soljakt & Værfilter
              </h3>
              <p className="text-2xs text-[var(--text-muted)]">
                Finn de nærmeste plassene med sol og godvær
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-input)] transition"
            aria-label="Lukk"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content Body */}
        <div className="py-4 space-y-5 overflow-y-auto custom-scrollbar flex-1">
          {/* Seksjon 1: Periode */}
          <div>
            <label className="text-2xs font-black uppercase tracking-wider text-[var(--text-secondary)] flex items-center gap-1.5 mb-2.5">
              <Calendar size={12} className="text-amber-400" />
              Når ønsker du sol?
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {PERIOD_OPTIONS.map((opt) => {
                const isSelected = period === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setPeriod(opt.id)}
                    className={`flex flex-col items-center justify-center p-2.5 rounded-xl border text-center transition active:scale-95 ${
                      isSelected
                        ? 'bg-amber-500/15 border-amber-500/50 text-[var(--text-primary)] shadow-sm'
                        : 'bg-[var(--bg-surface-alt)] border-[var(--border-secondary)] text-[var(--text-secondary)] hover:bg-[var(--bg-input)]'
                    }`}
                  >
                    <span className="text-base mb-0.5">{opt.emoji}</span>
                    <span className="text-xs font-black">{opt.label}</span>
                    <span className="text-4xs text-[var(--text-muted)] mt-0.5">
                      {opt.sublabel}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Seksjon 2: Maks kjøreavstand */}
          <div>
            <div className="flex items-center justify-between mb-2.5">
              <label className="text-2xs font-black uppercase tracking-wider text-[var(--text-secondary)] flex items-center gap-1.5">
                <MapPin size={12} className="text-emerald-400" />
                Maks avstand fra deg
              </label>
              <span className="text-xs font-black text-emerald-400">
                {maxDistanceKm === 0 ? 'Ubegrenset' : `${maxDistanceKm} km`}
              </span>
            </div>

            {/* Hurtigvalg chips */}
            <div className="grid grid-cols-4 gap-2 mb-3">
              {DISTANCE_PRESETS.map((preset) => {
                const isSelected = maxDistanceKm === preset;
                return (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setMaxDistanceKm(preset)}
                    className={`py-2 px-1 text-center rounded-xl border text-xs font-black transition active:scale-95 ${
                      isSelected
                        ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300'
                        : 'bg-[var(--bg-surface-alt)] border-[var(--border-secondary)] text-[var(--text-secondary)] hover:bg-[var(--bg-input)]'
                    }`}
                  >
                    {preset === 0 ? 'Ingen grense' : `${preset} km`}
                  </button>
                );
              })}
            </div>

            {/* Slider for finjustering */}
            <div className="px-1">
              <input
                type="range"
                min="25"
                max="350"
                step="25"
                value={maxDistanceKm === 0 ? 350 : maxDistanceKm}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  setMaxDistanceKm(val >= 350 ? 0 : val);
                }}
                className="w-full accent-emerald-500 cursor-pointer"
              />
              <div className="flex justify-between text-4xs text-[var(--text-muted)] mt-1 font-bold">
                <span>25 km</span>
                <span>100 km</span>
                <span>200 km</span>
                <span>350+ km</span>
              </div>
            </div>
          </div>

          {/* Seksjon 3: Værkrav */}
          <div>
            <label className="text-2xs font-black uppercase tracking-wider text-[var(--text-secondary)] flex items-center gap-1.5 mb-2.5">
              <Sliders size={12} className="text-blue-400" />
              Værstyrke
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setStrictSun(false)}
                className={`flex items-start gap-2.5 p-3 rounded-xl border text-left transition ${
                  !strictSun
                    ? 'bg-brand/15 border-brand/40 text-[var(--text-primary)]'
                    : 'bg-[var(--bg-surface-alt)] border-[var(--border-secondary)] text-[var(--text-secondary)]'
                }`}
              >
                <span className="text-lg">🌤️</span>
                <div className="min-w-0">
                  <div className="text-xs font-black">Pent vær & opphold</div>
                  <div className="text-4xs text-[var(--text-muted)] mt-0.5">
                    Solgløtt, delvis skyet og uten regn (anbefalt)
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setStrictSun(true)}
                className={`flex items-start gap-2.5 p-3 rounded-xl border text-left transition ${
                  strictSun
                    ? 'bg-amber-500/15 border-amber-500/40 text-[var(--text-primary)]'
                    : 'bg-[var(--bg-surface-alt)] border-[var(--border-secondary)] text-[var(--text-secondary)]'
                }`}
              >
                <span className="text-lg">☀️</span>
                <div className="min-w-0">
                  <div className="text-xs font-black">Kun strålende sol</div>
                  <div className="text-4xs text-[var(--text-muted)] mt-0.5">
                    Helt skyfritt eller nesten skyfritt vær
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="pt-3 border-t border-[var(--border-primary)] flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={handleReset}
            className="flex items-center gap-1 px-3 py-2 text-2xs font-bold text-[var(--text-muted)] hover:text-[var(--text-primary)] transition"
          >
            <RotateCcw size={12} />
            Nullstill
          </button>

          <button
            type="button"
            onClick={handleApply}
            className="flex-1 max-w-[200px] flex items-center justify-center gap-1.5 py-2.5 px-4 rounded-xl bg-amber-500 hover:bg-amber-400 text-stone-950 font-black text-xs uppercase tracking-wide transition shadow-lg shadow-amber-500/20 active:scale-95"
          >
            <Check size={14} strokeWidth={3} />
            {candidateCount !== undefined && candidateCount !== null
              ? `Bruk filter (${candidateCount})`
              : 'Bruk filter'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SunFilterModal;
